import BankIcon from 'assets/svg/icons/bankIcon';
import { Alert } from 'components/atoms/Alert';
import { Button } from 'components/atoms/Button';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import { Select } from 'components/atoms/Form/Select';
import { Toggle } from 'components/atoms/Form/Toggle';
import { Modal } from 'components/atoms/Modal';
import { RadioGroup } from 'components/atoms/RadioGroups';
import PaymentAmount from 'components/molecules/Fields/PaymentAmount/PaymentAmount.component';
import ModalHeading from 'components/molecules/ModalHeading';
import { joiResolver } from 'customJoi';
import dayjs from 'dayjs';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useFeatures from 'hooks/useFeatures';
import { motion } from 'motion/react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { BankAccountTypes } from 'types/banks.types';
import type { color } from 'types/shared/worlds';
import type { Connector } from 'types/transactions.types';
import { StandingOrderFrequency } from 'types/transactions.types';
import { AvailableCountries } from 'types/users.types';
import { fadeTransition } from 'utils/constants';
import { isTaxOfficeName } from 'utils/helpers';
import {
  CommunicationTypes,
  isCustomSubAccount,
  isTaxOfficeTransfer,
  isTaxReserveAccountableBank,
  TransferFieldNames,
} from '../../helpers';
import { useContactSearch } from '../hooks/useContactSearch/useContactSearch';
import TransferScheduleForm from '../../TransferScheduleForm.component';
import ContactOption from './ContactOption';
import BeneficiaryVerificationStatus from './BeneficiaryVerificationStatus';
import { isValidIBAN } from 'ibantools';
import { VERIFICATION_FIELD_THEME } from '../constants/verificationTheme';
import { externalTransferSchema } from '../constants/externalTransferSchema';

export interface ExternalInitialPaymentData {
  amount?: number;
  receiptAccount?: Connector | BankAccountTypes.external;
  sourceAccount?: string;
  recipient_iban?: string;
  recipient_name?: string;
  communication?: string;
  communication_type?: CommunicationTypes;
  instant?: boolean;
  period?: StandingOrderFrequency;
  firstExecutionDate?: Date;
  lastExecutionDate?: Date;
}

export interface ExternalTransferProps {
  initialData?: ExternalInitialPaymentData;
  onSubmit?: (values: Record<string, unknown>) => void;
  color?: color;
  loading?: boolean;
  formTitle?: string;
  formSubtitle?: string;
  formSubtitleClassName?: string;
  btnLabel?: string;
  buttonAmplitudeEvent?: string;
  isStandingOrderPayment?: boolean;
  sourceAccountBalance?: number;
  sourceAccount?: Connector;
  formType?: 'basket' | 'external';
}
const ExternalTransfer: FC<ExternalTransferProps> = ({
  initialData = {},
  onSubmit,
  color = 'bank',
  loading,
  formTitle,
  formSubtitle,
  formSubtitleClassName,
  btnLabel,
  buttonAmplitudeEvent,
  isStandingOrderPayment,
  sourceAccountBalance,
  sourceAccount,
  formType,
}) => {
  const { t } = useTranslation();

  const {
    recipient_name,
    amount = 0,
    recipient_iban,
    communication = '',
    instant,
    communication_type,
    period,
    firstExecutionDate,
    lastExecutionDate,
  } = initialData;

  const { data: user } = useGetCurrentCustomerData();
  const {
    searchByName,
    searchByIban,
    setCurrentContact,
    verifyContact,
    getCurrentContactVerification,
  } = useContactSearch();

  const IBANplaceholder = useFeatures('settings.IBANPlaceholder');

  const fromTaxReserveAccountableBank =
    isTaxReserveAccountableBank(sourceAccount);
  const fromSubAccount = isCustomSubAccount(sourceAccount!);

  const externalTransferFormAPI = useForm({
    mode: 'onChange',
    defaultValues: {
      ...(isStandingOrderPayment
        ? {
            [TransferFieldNames.period]:
              period || StandingOrderFrequency.Monthly,
            [TransferFieldNames.firstExecutionDate]:
              firstExecutionDate || dayjs().add(1, 'hour').toDate(),
            [TransferFieldNames.lastExecutionDate]: lastExecutionDate,
          }
        : {
            [TransferFieldNames.period]:
              period || StandingOrderFrequency.OneTime,
            [TransferFieldNames.firstExecutionDate]:
              firstExecutionDate || dayjs().toDate(),
            [TransferFieldNames.lastExecutionDate]: lastExecutionDate,
          }),
      [TransferFieldNames.recipient_name]: recipient_name,
      [TransferFieldNames.amount]: amount,
      [TransferFieldNames.recipient_iban]: recipient_iban,
      [TransferFieldNames.communication]: communication,
      [TransferFieldNames.communication_type]:
        user?.country === AvailableCountries.germany
          ? CommunicationTypes.unstructured
          : communication_type || CommunicationTypes.structured,
      [TransferFieldNames.instant]: instant,
    },
    resolver: joiResolver(externalTransferSchema(sourceAccountBalance), {
      allowUnknown: true,
      abortEarly: false,
    }),
  });

  const [
    communicationTypeValue,
    instantTransferValue,
    recipientNameValue,
    recipientIBANValue,
    periodValue,
    firstExecutionDateValue,
    lastExecutionDateValue,
  ] = externalTransferFormAPI.watch([
    TransferFieldNames.communication_type,
    TransferFieldNames.instant,
    TransferFieldNames.recipient_name,
    TransferFieldNames.recipient_iban,
    TransferFieldNames.period,
    TransferFieldNames.firstExecutionDate,
    TransferFieldNames.lastExecutionDate,
  ]);

  const firstExecutionDateIsToday = dayjs(firstExecutionDateValue).isSame(
    dayjs(),
    'day',
  );

  // Track last verified combination to prevent duplicate calls
  const [lastVerified, setLastVerified] = useState<{
    name: string;
    iban: string;
  } | null>(null);

  // Ref to store the debounce timeout
  const verificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Single verification function that handles both immediate and debounced calls
  const triggerVerification = useCallback(
    async (
      name: string,
      iban: string,
      options: { immediate?: boolean } = {},
    ) => {
      // Trim whitespace for comparison
      const trimmedName = name.trim();
      const trimmedIban = iban.trim();

      if (!trimmedName || !trimmedIban || !isValidIBAN(trimmedIban)) return;

      // Check if we already verified this exact combination (after trimming)
      if (
        !options.immediate &&
        lastVerified?.name === trimmedName &&
        lastVerified?.iban === trimmedIban
      ) {
        // Already verified this combination, skipping
        return;
      }

      const performVerification = async () => {
        try {
          await verifyContact(trimmedName, trimmedIban);
          setLastVerified({ name: trimmedName, iban: trimmedIban });
        } catch (error) {
          console.error('Verification failed:', error);
          // Still update lastVerified to prevent retries of failed combinations
          setLastVerified({ name: trimmedName, iban: trimmedIban });
        }
      };

      if (options.immediate) {
        // Immediate verification for dropdown selections
        await performVerification();
      } else {
        // Debounced verification for typing
        if (verificationTimeoutRef.current) {
          clearTimeout(verificationTimeoutRef.current);
        }
        verificationTimeoutRef.current = setTimeout(performVerification, 1000);
      }
    },
    [verifyContact, lastVerified],
  );

  // Handle name suggestion click
  const handleNameSuggestionClick = useCallback(
    (suggestedName: string) => {
      externalTransferFormAPI.setValue(
        TransferFieldNames.recipient_name,
        suggestedName,
      );

      const currentIban = externalTransferFormAPI.getValues(
        TransferFieldNames.recipient_iban,
      );

      if (currentIban) {
        triggerVerification(suggestedName, currentIban, {
          immediate: true,
        });
      }
    },
    [externalTransferFormAPI, triggerVerification],
  );

  // Simple helper to determine if we should show verification
  const shouldShowVerification = useCallback(
    (name?: string, iban?: string): boolean => {
      // Show verification when we have both fields, valid IBAN, and verification data
      return !!(
        name &&
        iban &&
        isValidIBAN(iban) &&
        getCurrentContactVerification()
      );
    },
    [getCurrentContactVerification],
  );

  // Verification theme options for field styling
  const verification = getCurrentContactVerification();
  const verificationThemeOptions =
    verification &&
    shouldShowVerification(recipientNameValue, recipientIBANValue)
      ? VERIFICATION_FIELD_THEME[verification.status] || {}
      : {};

  // One-time verification on mount. Necessary to do that with initial values because otherwise
  // there is no guarantee that the data will be available when the component mounts.
  useEffect(() => {
    // Mount: Verify initial values if present
    if (recipient_name && recipient_iban) {
      triggerVerification(recipient_name, recipient_iban, { immediate: true });
    }

    // Unmount: Cleanup debounce timeout
    return () => {
      if (verificationTimeoutRef.current) {
        clearTimeout(verificationTimeoutRef.current);
      }
    };
  }, []);

  return (
    <motion.div
      {...fadeTransition}
      key="1"
      className="flex items-center flex-col"
    >
      <ModalHeading
        Icon={BankIcon}
        title={formTitle || t('accountable_banking.external_transfer.title')}
        subtitle={
          formSubtitle || t('accountable_banking.external_transfer.subtitle')
        }
        subtitleClassName={formSubtitleClassName}
      />

      <FormProvider {...externalTransferFormAPI}>
        <form
          className="w-full"
          onSubmit={externalTransferFormAPI.handleSubmit(async (values) => {
            try {
              // Verify beneficiary before submitting if we have both name and IBAN
              let verificationToken: string | undefined;

              if (
                values[TransferFieldNames.recipient_name] &&
                values[TransferFieldNames.recipient_iban]
              ) {
                // Check if we already have verification data
                const existingVerification = getCurrentContactVerification();

                if (
                  existingVerification &&
                  existingVerification.name ===
                    values[TransferFieldNames.recipient_name] &&
                  existingVerification.iban ===
                    values[TransferFieldNames.recipient_iban]
                ) {
                  // Use existing verification token
                  verificationToken = existingVerification.verificationToken;
                } else {
                  // Perform new verification
                  const verificationResult = await verifyContact(
                    values[TransferFieldNames.recipient_name],
                    values[TransferFieldNames.recipient_iban],
                  );
                  verificationToken = verificationResult.verificationToken;
                }
              }

              // Include verification token in the form data
              const formDataWithVerification = {
                ...values,
                verificationToken,
              };

              onSubmit?.(formDataWithVerification);
            } catch (error) {
              console.error(
                'Verification failed during form submission:',
                error,
              );
              // Still submit the form without verification token
              onSubmit?.(values);
            }
          })}
        >
          <FieldConnector
            className="mt-3"
            name={TransferFieldNames.recipient_name}
            label={t(
              'accountable_banking.external_transfer.fields.recipient_name.label',
            )}
          >
            {({ field }) => (
              <Select
                {...field}
                value={field.value}
                asyncSearch
                name={TransferFieldNames.recipient_name}
                cacheKey={'ContactList'}
                mode="asyncOptionsPaginated"
                placeholder=""
                creatable
                components={{ Option: ContactOption }}
                onChange={async (option) => {
                  if (option?.iban) {
                    externalTransferFormAPI.setValue(
                      TransferFieldNames.recipient_iban,
                      option.iban,
                    );
                    // Trigger validation for the IBAN field that was just updated
                    externalTransferFormAPI.trigger(
                      TransferFieldNames.recipient_iban,
                    );
                  }

                  setCurrentContact(option);

                  const value = option?.value || '';
                  field.onChange(value);

                  // Trigger validation to ensure error state is updated
                  externalTransferFormAPI.trigger(
                    TransferFieldNames.recipient_name,
                  );

                  // Trigger verification if both fields are now complete (immediate for dropdown selection)
                  const currentIban =
                    option?.iban ||
                    externalTransferFormAPI.getValues(
                      TransferFieldNames.recipient_iban,
                    );
                  if (value && currentIban) {
                    triggerVerification(value, currentIban, {
                      immediate: true,
                    });
                  }

                  return value;
                }}
                fetchOnEmpty={false}
                onFetch={searchByName}
                onInputChange={(inputValue, actionMeta) => {
                  // Trigger debounced verification when user is typing
                  if (actionMeta.action === 'input-change') {
                    const currentIban = externalTransferFormAPI.getValues(
                      TransferFieldNames.recipient_iban,
                    );
                    if (inputValue && currentIban) {
                      triggerVerification(inputValue, currentIban);
                    }
                  }
                }}
                themeOptions={{
                  hideDropdownIndicator: true,
                  ...verificationThemeOptions,
                }}
              />
            )}
          </FieldConnector>
          {/* Show verification status when in verify mode */}
          {shouldShowVerification(recipientNameValue, recipientIBANValue) && (
            <BeneficiaryVerificationStatus
              verificationData={getCurrentContactVerification() ?? undefined}
              className="mt-2"
              variant="alternative"
              onNameSuggestionClick={handleNameSuggestionClick}
            />
          )}

          <FieldConnector
            name={TransferFieldNames.recipient_iban}
            className="mt-3"
            label={t(
              'accountable_banking.external_transfer.fields.recipient_iban.label',
            )}
          >
            {({ field }) => (
              <div>
                <Select
                  {...field}
                  value={field.value}
                  asyncSearch
                  name={TransferFieldNames.recipient_iban}
                  cacheKey={'ContactList'}
                  placeholder={IBANplaceholder}
                  mode="asyncOptionsPaginated"
                  creatable
                  components={{ Option: ContactOption }}
                  onChange={async (option) => {
                    if (option?.name) {
                      externalTransferFormAPI.setValue(
                        TransferFieldNames.recipient_name,
                        option.name,
                      );
                      // Trigger validation for the name field that was just updated
                      externalTransferFormAPI.trigger(
                        TransferFieldNames.recipient_name,
                      );
                    }

                    setCurrentContact(option);

                    const value = option?.value || '';
                    field.onChange(value);

                    // Trigger validation to ensure error state is updated
                    externalTransferFormAPI.trigger(
                      TransferFieldNames.recipient_iban,
                    );

                    // Trigger verification if both fields are now complete (immediate for dropdown selection)
                    const currentName =
                      option?.name ||
                      externalTransferFormAPI.getValues(
                        TransferFieldNames.recipient_name,
                      );
                    if (currentName && value) {
                      triggerVerification(currentName, value, {
                        immediate: true,
                      });
                    }

                    return value;
                  }}
                  fetchOnEmpty={false}
                  onFetch={searchByIban}
                  onInputChange={(inputValue, actionMeta) => {
                    // Trigger debounced verification when user is typing
                    if (actionMeta.action === 'input-change') {
                      const currentName = externalTransferFormAPI.getValues(
                        TransferFieldNames.recipient_name,
                      );
                      if (currentName && inputValue) {
                        triggerVerification(currentName, inputValue);
                      }
                    }
                  }}
                  themeOptions={{
                    hideDropdownIndicator: true,
                    ...verificationThemeOptions,
                  }}
                />

                {isTaxOfficeTransfer(recipientNameValue, recipientIBANValue) ? (
                  <Alert
                    className="mt-2"
                    type="info"
                    title={t(
                      'accountable_banking.external_transfer.tax_office_alert.confirmed',
                    )}
                  />
                ) : isTaxOfficeName(recipientNameValue) &&
                  recipientIBANValue ? (
                  <Alert
                    className="mt-2"
                    type="warning"
                    title={t(
                      'accountable_banking.external_transfer.tax_office_alert.unconfirmed',
                    )}
                  />
                ) : null}
              </div>
            )}
          </FieldConnector>

          <FieldConnector
            className="mt-3"
            name={TransferFieldNames.amount}
            label={t('amountSign')}
          >
            {({ field }) => <PaymentAmount {...field} />}
          </FieldConnector>

          <FieldConnector
            className="mt-3"
            name={TransferFieldNames.communication_type}
            label={t(
              'accountable_banking.external_transfer.fields.communication.label',
            )}
            showUI={user?.country === AvailableCountries.belgium}
          >
            {({ field }) => (
              <RadioGroup
                {...field}
                type="inline"
                labelClassNames="text-black text-sm"
                data={[
                  {
                    title: t(
                      'accountable_banking.external_transfer.fields.communication.structured',
                    ),
                    id: 'structured',
                    checked: communicationTypeValue === 'structured',
                  },
                  {
                    title: t(
                      'accountable_banking.external_transfer.fields.communication.unstructured',
                    ),
                    id: 'unstructured',
                    checked: communicationTypeValue === 'unstructured',
                  },
                ]}
              />
            )}
          </FieldConnector>

          <FieldConnector
            className="mt-3"
            name={TransferFieldNames.communication}
            label={
              user?.country === AvailableCountries.germany
                ? t(
                    'accountable_banking.external_transfer.fields.communication.label',
                  )
                : undefined
            }
          >
            {({ field }) => (
              <FreeTextInput
                {...field}
                key={communicationTypeValue}
                type={
                  communicationTypeValue === CommunicationTypes.structured
                    ? 'maskedInput'
                    : 'text'
                }
                placeholder={
                  communicationTypeValue === CommunicationTypes.structured
                    ? '++++XXX/XXXX/XXXX+++'
                    : ''
                }
                maskProps={
                  communicationTypeValue === CommunicationTypes.structured
                    ? {
                        mask: '+++999/9999/99999+++',
                        maskChar: '',
                        alwaysShowMask: false,
                        formatChars: {
                          '9': '[0-9]',
                          a: '[A-Za-z]',
                          '*': '[A-Za-z0-9]',
                          S: '[A-Za-z0-9]',
                        },
                      }
                    : undefined
                }
                onChange={({ target: { value } }) => {
                  const _value = value || '';
                  field.onChange(_value);
                }}
              />
            )}
          </FieldConnector>

          {formType !== 'basket' &&
          !fromTaxReserveAccountableBank &&
          !fromSubAccount ? (
            <Modal
              name="standing-order-fields"
              trigger={({ openModal }) => (
                <div className="my-3">
                  <label
                    htmlFor="standing-order-fields"
                    className="first-letter:capitalize font-semibold text-sm text-primary-800"
                  >
                    {t(
                      'accountable_banking.scheduled_payments.schedule_form.when',
                    )}
                  </label>

                  <Select
                    name="ui"
                    value={`${t(periodValue)}, ${
                      firstExecutionDateIsToday
                        ? t('today')
                        : dayjs(firstExecutionDateValue).format('DD MMMM YYYY')
                    }`}
                    openMenuOnClick
                    isSearchable={false}
                    noOptionsMessage={() => null}
                    placeholder=""
                    onMenuOpen={() => {
                      openModal();
                    }}
                    themeOptions={{ hideDropdownIndicator: true }}
                  />
                </div>
              )}
              render={({ closeModal }) => (
                <TransferScheduleForm
                  sourceAccount={sourceAccount}
                  defaultValues={{
                    [TransferFieldNames.period]: periodValue,
                    [TransferFieldNames.firstExecutionDate]:
                      firstExecutionDateValue,
                    [TransferFieldNames.lastExecutionDate]:
                      lastExecutionDateValue,
                  }}
                  onSubmit={(schedule) => {
                    externalTransferFormAPI.setValue(
                      TransferFieldNames.period,
                      schedule[TransferFieldNames.period],
                    );

                    externalTransferFormAPI.setValue(
                      TransferFieldNames.firstExecutionDate,
                      schedule[TransferFieldNames.firstExecutionDate],
                    );

                    externalTransferFormAPI.setValue(
                      TransferFieldNames.lastExecutionDate,
                      schedule[TransferFieldNames.lastExecutionDate],
                    );

                    closeModal();
                  }}
                />
              )}
            />
          ) : null}

          <div className="flex items-center mt-3 space-x-4">
            <Toggle
              color={color}
              enabled={instantTransferValue}
              onChange={(value) => {
                externalTransferFormAPI.setValue(
                  TransferFieldNames.instant,
                  value,
                );
              }}
            />

            <span className="text-primary-700">
              {t(
                'accountable_banking.external_transfer.fields.internal_transfer.label',
              )}
            </span>
          </div>

          <Button
            className="w-full mt-12"
            type="submit"
            loading={loading}
            tracingEvent={buttonAmplitudeEvent}
          >
            {btnLabel || t('continue')}
          </Button>
        </form>
      </FormProvider>
    </motion.div>
  );
};

export default ExternalTransfer;
